/**
 * Calendar view of an outing.
 *
 * Public, like the rest of the share link. The page renders the projected
 * timeline and nothing else: the three export formats live on their own route so
 * that this page stays a read-only view.
 */

import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { Timeline } from '../../components/calendar/timeline'
import { PageHeader } from '../../components/layout/page-header'
import { ErrorBlock, SkeletonCard } from '../../components/layout/states'
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { EmptyState, Stat } from '../../components/ui/primitives'
import { useCalendarView } from '../../hooks/use-shared'
import { formatLocalDateTime } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { useSharedOutlet } from './shared-layout'

export function CalendarPage() {
  const { t } = useTranslation()
  const { shareToken, event } = useSharedOutlet()
  const calendarQuery = useCalendarView(shareToken)
  const locale = currentLocale()

  /**
   * The timeline is derived from the activity durations, so it can run past the
   * end the organizer typed. Saying so avoids a plan that quietly contradicts
   * the event's own time.
   */
  const lastEntry = calendarQuery.data?.timeline.at(-1)
  const overrun =
    lastEntry && event.eventDateEnd && lastEntry.endAt > event.eventDateEnd
      ? { plannedEnd: lastEntry.endAt, declaredEnd: event.eventDateEnd }
      : null

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: event.title, to: `/e/${shareToken}` },
          { label: t('calendar.title') },
        ]}
        title={t('calendar.title')}
        actions={
          <Button variant="outline" asChild>
            <Link to={`/e/${shareToken}/calendar/export`}>{t('calendar.export')}</Link>
          </Button>
        }
      />

      {calendarQuery.isPending ? (
        <SkeletonCard />
      ) : calendarQuery.isError || !calendarQuery.data ? (
        <ErrorBlock
          title={t('error.internal_error')}
          onRetry={() => void calendarQuery.refetch()}
          retryLabel={t('common.retry')}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{t('calendar.timeline')}</CardTitle>
            {calendarQuery.data.decided ? (
              <Stat
                label={t('calendar.decidedArea')}
                value={calendarQuery.data.decided.districtName}
              />
            ) : null}
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {overrun ? (
              <Alert variant="warning">
                <AlertTitle>{t('calendar.overrunTitle')}</AlertTitle>
                <AlertDescription>
                  {t('calendar.overrunBody', {
                    end: formatLocalDateTime(overrun.plannedEnd, locale),
                    declared: formatLocalDateTime(overrun.declaredEnd, locale),
                  })}
                </AlertDescription>
              </Alert>
            ) : null}
            {calendarQuery.data.timeline.length === 0 ? (
              <EmptyState title={t('common.empty')} />
            ) : (
              <Timeline
                startAt={calendarQuery.data.event.eventDateStart}
                endAt={calendarQuery.data.event.eventDateEnd}
                entries={calendarQuery.data.timeline}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
